import { useEffect, useRef, useState } from "react";

import {
  Loader,
  Modal,
  ModalHeader,
  Notification,
  NotificationType,
  Slide,
  Slider,
  useSlider,
} from "@transfr-inc/dashboard-components";

import {
  trackClassroomsArchived,
  trackLearnersArchived,
  trackSoleClassroomWarningSelected,
  trackSoleClassroomWarningViewed,
} from "@lib/event-trackers";

import { useCurrentUser } from "@queries";

import { useArchiveClassrooms } from "./api";
import {
  ArchiveClassroomsConfirmSlide,
  ArchiveClassroomsResultSlide,
} from "./components";

import "./archive-classrooms.modal.scss";

const TOTAL_SLIDES = 2;

export const ArchiveClassroomsModal = ({
  open,
  classrooms = [],
  xapiConfig,
  invalidateOnSuccess,
  onCancel,
  onSuccess,
  onError,
}) => {
  const { source = "list" } = xapiConfig ?? {};
  const { organizationCode } = useCurrentUser() || {};
  const slider = useSlider({ totalSlides: TOTAL_SLIDES });
  const {
    mutateAsync: archiveClassrooms,
    isPending,
    error,
    reset,
  } = useArchiveClassrooms({ invalidateOnSuccess });
  const [firstResult, setFirstResult] = useState();
  const batchIdRef = useRef();

  useEffect(() => {
    if (open) {
      if (classrooms?.length === 0) {
        onError?.("No classrooms selected to archive.");
        onCancel?.();
      } else {
        slider.goSlide(0);
      }
    } else {
      reset();
      setFirstResult();
      batchIdRef.current = undefined;
    }
  }, [open]);

  const trackArchived = (classrooms) => {
    trackClassroomsArchived(classrooms, {
      source,
      batchId: batchIdRef.current,
    });
  };

  const trackArchivedWithLearners = (classrooms) => {
    const blockedClassrooms = classrooms.map((c) => ({
      ...c,
      learnersAlsoArchived: true,
      learnerArchiveCount: c?.users?.length ?? 0,
    }));

    const seen = new Set();
    const uniqueArchivedUsers = blockedClassrooms
      .flatMap((b) => b.users ?? [])
      .filter((u) => !seen.has(u.userId) && seen.add(u.userId));

    Promise.allSettled([
      trackClassroomsArchived(blockedClassrooms, {
        source,
        batchId: batchIdRef.current,
      }),
      trackLearnersArchived(uniqueArchivedUsers, {
        source,
        triggeredByClassroomArchive: true,
        batchId: batchIdRef.current,
      }),
    ]);
  };

  const trackWarningResolved = (classrooms, userProceeded) => {
    const users = classrooms.flatMap((b) => b.users ?? []);
    const uniqueUserCount = new Set(users.map((u) => u.userId)).size;

    trackSoleClassroomWarningSelected(
      uniqueUserCount,
      classrooms.length > 1,
      userProceeded
    );
  };

  const handleArchive = async () => {
    const classroomIds = classrooms.map((c) => c.classroomId);
    batchIdRef.current = crypto.randomUUID();

    try {
      const result = await archiveClassrooms({ classroomIds, organizationCode });

      trackArchived(
        classrooms.filter((c) => result.archived?.includes(c.classroomId))
      );

      if (result.blocked?.length > 0) {
        trackSoleClassroomWarningViewed();
        setFirstResult(result);
        slider.nextSlide();
      } else {
        onSuccess?.(result);
        onCancel?.();
      }
    } catch (error) {
      onError?.(error);
    }
  };

  const handleArchiveAll = async () => {
    const classroomIds = firstResult.blocked.map((b) => b.classroomId);

    trackWarningResolved(firstResult.blocked, true);

    const result = await archiveClassrooms({
      classroomIds,
      organizationCode,
      options: { archiveUsers: true },
    });

    const archivedClassroomsWithLearners = firstResult.blocked.filter((b) =>
      result.archived.includes(b.classroomId)
    );

    trackArchivedWithLearners(archivedClassroomsWithLearners);

    onSuccess?.({
      archived: [...(firstResult.archived ?? []), ...(result.archived ?? [])],
      blocked: result.blocked ?? [],
    });
    onCancel?.();
  };

  const handleSkipBlocked = () => {
    trackWarningResolved(firstResult.blocked, false);

    onSuccess?.({
      archived: firstResult.archived ?? [],
      blocked: firstResult.blocked ?? [],
    });
    onCancel?.();
  };

  return (
    <Modal
      open={open}
      backdropClassName="archive-classrooms-modal"
      onClose={onCancel}
    >
      {isPending && <Loader overlay />}
      <ModalHeader
        icon={["fa-regular", "box-archive"]}
        title="Archive Classrooms"
      />
      {error && (
        <div className="archive-classrooms-error-container">
          <Notification type={NotificationType.error} closable onClose={reset}>
            {error.data?.detail ?? "Something went wrong. Please try again."}
          </Notification>
        </div>
      )}
      <Slider
        totalSlides={TOTAL_SLIDES}
        currentSlideIndex={slider.currentIndex}
      >
        <Slide>
          <ArchiveClassroomsConfirmSlide
            classrooms={classrooms}
            onCancel={onCancel}
            onConfirm={handleArchive}
          />
        </Slide>
        <Slide>
          <ArchiveClassroomsResultSlide
            classrooms={classrooms}
            blocked={firstResult?.blocked}
            onCancel={handleSkipBlocked}
            onConfirm={handleArchiveAll}
          />
        </Slide>
      </Slider>
    </Modal>
  );
};
